import { z } from 'zod'

export const DAY_PRESETS = [1, 3, 7] as const
export const VMJ_DAYS_OPTIONS = [3, 5, 7] as const
export const SEASON_MODE_OPTIONS = [
  { value: 'monthly', label: 'mensuelle' },
  { value: 'monthlyInterpolated', label: 'interpolée' },
  { value: 'weekly', label: 'hebdo' },
] as const

export const INTRADAY_HOUR_OPTIONS = [9, 11, 14, 18, 22] as const
export const DEFAULT_INTRADAY_HOUR = 14

export const COHORT_HORIZON_OPTIONS = [7, 14, 31] as const
export const DEFAULT_COHORT_HORIZON = 31

export const shortTermSearchSchema = z.object({
  mode: z.enum(['stores', 'projects', 'cohorte']).optional().default('stores'),
  storeIds: z.array(z.number().int().positive()).optional().default([]),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  btStartDate: z.string().optional(),
  btEndDate: z.string().optional(),
  productIds: z.array(z.number().int().positive()).optional().default([]),
  poolIds: z.array(z.number().int().positive()).optional().default([]),
  tab: z.enum(['forecast', 'backtest', 'intraday', 'season']).optional().default('forecast'),
  intradayHour: z.number().int().min(1).max(23).optional().default(DEFAULT_INTRADAY_HOUR),
  intradayDate: z.string().optional(),
  corrections: z.boolean().optional().default(false),
  vmjDays: z.number().int().min(1).max(14).optional().default(5),
  weekdaysOnly: z.boolean().optional().default(true),
  productDayCoefs: z.boolean().optional().default(true),
  seasonMode: z.enum(['monthly', 'monthlyInterpolated', 'weekly']).optional().default('weekly'),
  cohortTab: z.enum(['courbe', 'backtest']).optional().default('courbe'),
  cohortHorizon: z.literal(COHORT_HORIZON_OPTIONS).catch(DEFAULT_COHORT_HORIZON),
  cohortStart: z.string().optional(),
  panelIds: z.array(z.number().int().positive()).optional().default([]),
})

export type ShortTermSearch = z.infer<typeof shortTermSearchSchema>
export type SeasonMode = ShortTermSearch['seasonMode']

export const dayCoefModeOf = (productDayCoefs: boolean): 'store' | 'product' =>
  productDayCoefs ? 'product' : 'store'

export const isoDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

const shiftedDay = (offset: number): string => {
  const date = new Date()
  date.setDate(date.getDate() + offset)
  return isoDate(date)
}

export const presetForecastRange = (days: number): { start: string; end: string } => ({
  start: shiftedDay(1),
  end: shiftedDay(days),
})

export const presetBacktestRange = (days: number): { start: string; end: string } => ({
  start: shiftedDay(-days),
  end: shiftedDay(-1),
})

export const defaultCohortStart = (): string => shiftedDay(1)

export const defaultForecastRange = (): { start: string; end: string } => presetForecastRange(7)

export const defaultBacktestRange = (): { start: string; end: string } => presetBacktestRange(7)
